import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import ReactApexChart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import { pmService, type Patient, type PayerPriority } from "../../services/pm.service";
import { SearchableMultiSelect } from "../ui/SearchableMultiSelect";
import { Select } from "../ui/Select";
import { DatePicker } from "../ui/DatePicker";
import { computePeriodRange, type PmPeriodType } from "../../utils/period";

const PAGE_SIZE = 50;

const formatDate = (dateStr: string): string => {
  if (!dateStr) return "—";
  const d = new Date(dateStr + "T00:00:00");
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};
const TOOLTIP_OFFSET = 12;

interface BarTooltipState {
  insurer: string;
  count: number;
  x: number;
  y: number;
}

interface AppliedFilters {
  insurers: string[];
  diagnoses: string[];
  cptCodes: string[];
  periodType: PmPeriodType;
  customFrom: string;
  customTo: string;
  priority: PayerPriority;
}

const arrsEqual = (a: string[], b: string[]) =>
  a.length === b.length && a.every((v) => b.includes(v));

const defaultCustom = computePeriodRange("current_month");

export const PatientFilterWidget: React.FC = () => {
  const [insurers, setInsurers] = useState<string[]>([]);
  const [diagnoses, setDiagnoses] = useState<string[]>([]);
  const [cptCodes, setCptCodes] = useState<string[]>([]);
  const [periodType, setPeriodType] = useState<PmPeriodType>("current_month");
  const [customFrom, setCustomFrom] = useState(defaultCustom.dateFrom);
  const [customTo, setCustomTo] = useState(defaultCustom.dateTo);
  const [priority, setPriority] = useState<PayerPriority>("primary");
  const [page, setPage] = useState(1);
  const [showTable, setShowTable] = useState(false);
  const [barTooltip, setBarTooltip] = useState<BarTooltipState | null>(null);
  const distributionRef = useRef<{ insurer: string; count: number }[]>([]);
  const chartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const hide = () => setBarTooltip(null);
    window.addEventListener("scroll", hide, { capture: true, passive: true });
    return () => window.removeEventListener("scroll", hide, { capture: true });
  }, []);

  const clampedPos = (x: number, y: number) => {
    const W = 220;
    const H = 80;
    if (!chartRef.current) return { top: y + TOOLTIP_OFFSET, left: x + TOOLTIP_OFFSET };
    const r = chartRef.current.getBoundingClientRect();
    return {
      top: Math.min(Math.max(y + TOOLTIP_OFFSET, r.top), r.bottom - H),
      left: Math.min(Math.max(x + TOOLTIP_OFFSET, r.left), r.right - W),
    };
  };

  const [applied, setApplied] = useState<AppliedFilters>({
    insurers: [],
    diagnoses: [],
    cptCodes: [],
    periodType: "current_month",
    customFrom: defaultCustom.dateFrom,
    customTo: defaultCustom.dateTo,
    priority: "primary",
  });

  const hasChanges =
    !arrsEqual(insurers, applied.insurers) ||
    !arrsEqual(diagnoses, applied.diagnoses) ||
    !arrsEqual(cptCodes, applied.cptCodes) ||
    periodType !== applied.periodType ||
    priority !== applied.priority ||
    (periodType === "custom" &&
      (customFrom !== applied.customFrom || customTo !== applied.customTo));

  const handleApply = () => {
    setApplied({ insurers, diagnoses, cptCodes, periodType, customFrom, customTo, priority });
    setPage(1);
  };

  const { dateFrom, dateTo } = computePeriodRange(
    applied.periodType,
    applied.customFrom,
    applied.customTo
  );

  const { data: insurersData } = useQuery({
    queryKey: ["pm-insurers"],
    queryFn: () => pmService.getInsurers(),
    staleTime: 5 * 60 * 1000,
  });

  const allInsurers = insurersData?.insurers ?? [];
  // "all selected" is semantically equivalent to "no filter"
  const appliedInsurers =
    applied.insurers.length > 0 && applied.insurers.length < allInsurers.length
      ? applied.insurers
      : [];

  const { data: diagnosesData } = useQuery({
    queryKey: ["pm-diagnoses"],
    queryFn: () => pmService.getDiagnoses(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: cptData } = useQuery({
    queryKey: ["pm-cpt-codes"],
    queryFn: () => pmService.getCptCodes(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: result, isLoading } = useQuery({
    queryKey: [
      "pm-patients",
      appliedInsurers,
      applied.diagnoses,
      applied.cptCodes,
      dateFrom,
      dateTo,
      applied.priority,
      page,
    ],
    queryFn: () =>
      pmService.searchPatients({
        insurers: appliedInsurers.length > 0 ? appliedInsurers : undefined,
        diagnoses: applied.diagnoses.length > 0 ? applied.diagnoses : undefined,
        cptCodes: applied.cptCodes.length > 0 ? applied.cptCodes : undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        priority: applied.priority,
        page,
        limit: PAGE_SIZE,
      }),
  });

  // const showDistribution = appliedInsurers.length === 0;
  const { data: distResult } = useQuery({
    queryKey: [
      "pm-distribution",
      applied.insurers,
      applied.diagnoses,
      applied.cptCodes,
      dateFrom,
      dateTo,
      applied.priority,
    ],
    queryFn: () =>
      pmService.getPatientDistribution({
        insurers: appliedInsurers.length > 0 ? appliedInsurers : undefined,
        diagnoses: applied.diagnoses.length > 0 ? applied.diagnoses : undefined,
        cptCodes: applied.cptCodes.length > 0 ? applied.cptCodes : undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        priority: applied.priority,
      }),
    enabled: true,
  });

  // Grand total without insurer filter — used as denominator for tooltip %
  const { data: grandTotalResult } = useQuery({
    queryKey: [
      "pm-patients-grand-total",
      applied.diagnoses,
      applied.cptCodes,
      dateFrom,
      dateTo,
      applied.priority,
    ],
    queryFn: () =>
      pmService.searchPatients({
        diagnoses: applied.diagnoses.length > 0 ? applied.diagnoses : undefined,
        cptCodes: applied.cptCodes.length > 0 ? applied.cptCodes : undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        priority: applied.priority,
        page: 1,
        limit: 1,
      }),
    enabled: appliedInsurers.length > 0,
  });

  const patients: Patient[] = result?.patients ?? [];
  const total = result?.total ?? 0;
  const totalPages = result?.totalPages ?? 0;
  const grandTotal = appliedInsurers.length > 0 ? (grandTotalResult?.total ?? total) : total;

  const distribution = distResult?.distribution ?? [];
  distributionRef.current = distribution;
  const barCategories = distribution.map((d) => d.insurer);
  const barValues = distribution.map((d) => d.count);
  const chartHeight = Math.max(200, barCategories.length * 32 + 50);

  const barOptions: ApexOptions = {
    chart: {
      type: "bar",
      toolbar: {
        show: true,
        tools: {
          download: true,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false,
          selection: false,
        },
        offsetX: -10,
        offsetY: 15,
      },
      animations: { enabled: false },
      offsetY: -20,
      events: {
        dataPointMouseEnter: (
          _e: MouseEvent,
          _chart: unknown,
          opts: { dataPointIndex: number }
        ) => {
          const d = distributionRef.current[opts.dataPointIndex];
          if (d)
            setBarTooltip({ insurer: d.insurer, count: d.count, x: _e.clientX, y: _e.clientY });
        },
        dataPointMouseLeave: () => setBarTooltip(null),
      },
    },
    plotOptions: {
      bar: { horizontal: true, barHeight: "50%", dataLabels: { position: "top" } },
    },
    dataLabels: {
      enabled: true,
      formatter: (val: number) => `${val}`,
      style: { fontSize: "11px", colors: ["#699CBB"], fontWeight: "bold" },
      offsetX: 20,
    },
    colors: ["#699CBB"],
    xaxis: {
      categories: barCategories,
      labels: { style: { fontSize: "11px", colors: "#6b7280" } },
      axisBorder: { show: true, color: "#BCBDC5" },
      axisTicks: { show: true, color: "#BCBDC5" },
    },
    yaxis: {
      labels: { style: { fontSize: "11px" }, maxWidth: 200 },
    },
    tooltip: { enabled: false },
    grid: {
      strokeDashArray: 3,
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: false } },
      padding: { right: 50 },
    },
    legend: { show: false },
  };

  return (
    <div className="bg-white border-2 border-border-gray flex flex-col h-full">
      {/* Header */}
      <div className="bg-primary-blue text-white px-4 md:px-6 py-3">
        <h3 className="font-bold text-lg">Patient Analytics</h3>
        <span className="text-xs sm:text-sm">
          Filter patients by insurer, diagnosis, and CPT code
        </span>
      </div>

      {/* Filters */}
      <div className="p-4 bg-gray-50 border-b-2 border-border-gray flex flex-wrap gap-3 items-end">
        <div className="flex-shrink-0">
          <label className="block text-xs font-medium text-gray-700 mb-1">Period</label>
          <Select
            value={periodType}
            onChange={(e) => setPeriodType(e.target.value as PmPeriodType)}
          >
            <option value="current_week">Current Week</option>
            <option value="previous_week">Previous Week</option>
            <option value="current_month">Current Month</option>
            <option value="previous_month">Previous Month</option>
            <option value="last_90_days">Last 90 Days</option>
            <option value="year_to_date">Year to Date</option>
            <option value="last_year">Last Year</option>
            <option value="custom">Custom Period</option>
          </Select>
        </div>

        {periodType === "custom" && (
          <>
            <div className="flex-shrink-0">
              <label className="block text-xs font-medium text-gray-700 mb-1">Start date</label>
              <DatePicker
                value={customFrom}
                maxDate={customTo || undefined}
                onChange={setCustomFrom}
              />
            </div>
            <div className="flex-shrink-0">
              <label className="block text-xs font-medium text-gray-700 mb-1">End date</label>
              <DatePicker
                value={customTo}
                minDate={customFrom || undefined}
                onChange={setCustomTo}
              />
            </div>
          </>
        )}

        <div className="flex-shrink-0">
          <label className="block text-xs font-medium text-gray-700 mb-1">Insurer</label>
          <SearchableMultiSelect
            options={insurersData?.insurers ?? []}
            selectedOptions={insurers}
            onSelectionChange={setInsurers}
            label="Insurers"
            emptyLabel="All Insurers"
          />
        </div>

        <div className="flex-shrink-0">
          <label className="block text-xs font-medium text-gray-700 mb-1">Diagnoses</label>
          <SearchableMultiSelect
            options={diagnosesData?.diagnoses ?? []}
            selectedOptions={diagnoses}
            onSelectionChange={setDiagnoses}
            label="Diagnoses"
            emptyLabel="All Diagnoses"
          />
        </div>

        <div className="flex-shrink-0">
          <label className="block text-xs font-medium text-gray-700 mb-1">CPT Codes</label>
          <SearchableMultiSelect
            options={cptData?.cptCodes ?? []}
            selectedOptions={cptCodes}
            onSelectionChange={setCptCodes}
            label="CPT Codes"
            emptyLabel="All CPT Codes"
          />
        </div>

        {/* Payor dropdown */}
        <div className="flex-shrink-0">
          <label className="block text-xs font-medium text-gray-700 mb-1">Payor</label>
          <Select value={priority} onChange={(e) => setPriority(e.target.value as PayerPriority)}>
            <option value="all">All</option>
            <option value="primary">Primary</option>
            <option value="secondary">Secondary</option>
            <option value="primary_secondary">Primary+Secondary</option>
            <option value="tertiary">Tertiary</option>
          </Select>
        </div>

        <button
          type="button"
          onClick={handleApply}
          disabled={!hasChanges}
          className="h-10 px-6 bg-primary-blue text-white text-xs sm:text-sm hover:bg-indigo-darker-blue disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap flex-shrink-0 transition-colors"
        >
          Apply
        </button>
      </div>

      {/* Content */}
      <div className="flex flex-col flex-1">
        {isLoading && <div className="text-center py-8 text-gray-500">Loading patients...</div>}

        {!isLoading && total === 0 && (
          <div className="text-center py-8 text-gray-500">No patients found.</div>
        )}

        {!isLoading && total > 0 && (
          <>
            <div className="bg-gray-50 border-b-2 border-border-gray px-4 md:px-6 py-2 text-xs font-bold text-gray-700 uppercase tracking-wide">
              {total.toLocaleString()} patients found
            </div>

            {/* Show distribution — pinned below stats bar */}
            {showTable && (
              <button
                type="button"
                onClick={() => setShowTable(false)}
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-gray-100 border-b-2 border-border-gray text-xs font-medium text-gray-700 hover:bg-gray-200 transition-colors"
              >
                Show distribution <span>↓</span>
              </button>
            )}

            {/* Sliding panels container */}
            <div
              ref={chartRef}
              className="relative overflow-hidden flex-1"
              style={{ minHeight: "400px" }}
            >
              {/* Chart panel */}
              <div className="absolute inset-0 overflow-y-auto overflow-x-hidden">
                <ReactApexChart
                  options={barOptions}
                  series={[{ name: "Patients", data: barValues }]}
                  type="bar"
                  height={chartHeight}
                />
              </div>

              {/* Table overlay — slides up from bottom */}
              <div
                className="absolute inset-0 bg-white flex flex-col"
                style={{
                  transform: showTable ? "translateY(0)" : "translateY(100%)",
                  transition: "transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
                  zIndex: 20,
                }}
              >
                {/* Table */}
                <div className="overflow-x-auto overflow-y-auto flex-1">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b-2 border-border-gray sticky top-0 z-10">
                      <tr>
                        <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase whitespace-nowrap">
                          Patient
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase whitespace-nowrap">
                          Date of Service
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase whitespace-nowrap">
                          Last Visit
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase whitespace-nowrap">
                          Insurer
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase whitespace-nowrap">
                          Diagnoses
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase whitespace-nowrap">
                          CPT Codes
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border-gray">
                      {patients.map((p) => (
                        <tr key={`${p.patientId}-${p.insurer}`} className="hover:bg-gray-50">
                          <td className="px-3 py-2 whitespace-nowrap font-medium">
                            {p.firstName} {p.lastName}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                            {formatDate(p.dateOfService)}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                            {formatDate(p.lastVisit)}
                          </td>
                          <td className="px-3 py-2 text-gray-600">{p.insurer || "—"}</td>
                          <td className="px-3 py-2">
                            <div className="flex flex-wrap gap-1 max-h-16 overflow-y-auto">
                              {p.diagnoses.map((d) => (
                                <span
                                  key={d}
                                  className="inline-block bg-blue-50 text-blue-700 text-xs px-1.5 py-0.5 rounded"
                                >
                                  {d}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex flex-wrap gap-1 max-h-16 overflow-y-auto">
                              {p.cptCodes.map((c) => (
                                <span
                                  key={c}
                                  className="inline-block bg-green-50 text-green-700 text-xs px-1.5 py-0.5 rounded"
                                >
                                  {c}
                                </span>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination inside overlay */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between py-3 px-2 border-t-2 border-border-gray flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="px-4 py-2 text-xs border-2 border-border-gray bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                    >
                      Previous
                    </button>
                    <span className="text-xs text-gray-600">
                      {(page - 1) * PAGE_SIZE + 1}–
                      {Math.min(page * PAGE_SIZE, total).toLocaleString()} of{" "}
                      {total.toLocaleString()}
                    </span>
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="px-4 py-2 text-xs border-2 border-border-gray bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Bottom toggle — pinned to widget bottom */}
            {!showTable && (
              <button
                type="button"
                onClick={() => setShowTable(true)}
                className="mt-auto w-full flex items-center justify-center gap-2 py-2.5 bg-gray-100 border-t-2 border-border-gray text-xs font-medium text-gray-700 hover:bg-gray-200 transition-colors"
              >
                Show patients <span>↑</span>
              </button>
            )}
          </>
        )}
      </div>

      {barTooltip &&
        createPortal(
          <div
            style={{
              position: "fixed",
              ...clampedPos(barTooltip.x, barTooltip.y),
              zIndex: 9999,
              pointerEvents: "none",
            }}
            className="bg-white border border-gray-200 shadow-lg rounded px-3 py-2 text-xs"
          >
            <div className="font-semibold text-gray-800 mb-1">{barTooltip.insurer}</div>
            <div className="text-gray-600">
              Patients:{" "}
              <span className="font-semibold text-gray-800">
                {barTooltip.count.toLocaleString()}
                {grandTotal > 0 &&
                  ` (${parseFloat(((barTooltip.count / grandTotal) * 100).toFixed(2))}%)`}
              </span>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};
